import PropTypes from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';
import Helmet from 'react-helmet';
import _ from 'lodash';

import { getScreenWidth, errorLogger, makeCanonicalTagObj, makeHreflangTagArr } from '@/utils';
import { Hr, PlaceholderWrapper } from '@/pages/NewsAndEventsDetailsPage/NewsAndEventsDetailsPage.style';

import { fetchNewsAndEvent } from '../../redux/actions/newsAndEvents';
import { getPageData } from '../../redux/selectors/pages';

import CarouselSection from '../../components/Article/CarouselSection';
import GallerySection from '../../components/Article/GallerySection';
import LinkSection from '../../components/Article/LinkSection';
import OverviewSection from '../../components/Article/OverviewSection';
import QuickLinksSection from '../../components/QuickLinksSection';
import SectionHeader from '../../components/Article/SectionHeader';
import SocialShareSection from '../../components/SocialShareSection/SocialShareSection';
import TextPanelsSection from '../../components/Article/TextPanelsSection';
import TextSection from '../../components/Article/TextSection';
import TopicToutSection from '../../components/Article/TopicToutSection';
import VideoSection from '../../components/Article/VideoSection';
import ImagePanelsSection from '../../components/Article/ImagePanelsSection';
import YoutubeSection from '../../components/Article/YoutubeSection';
import ScrollToTopButton from '../../components/ScrollToTopButton';
import BreadCrumbMenu from '../../components/BreadCrumbMenus/';
import Placeholder from '../../components/Placeholder';
import { TABLET_L, MOBILE_PADDING } from '../../constants';

class NewsAndEventsDetailsPage extends React.Component {
  static defaultProps = {
    data: {
      noLoaded: true,
      sections: [],
    },
    screenSize: null,
  };

  static propTypes = {
    data: PropTypes.shape(),
    fetchNewsAndEvent: PropTypes.func.isRequired,
    locale: PropTypes.string.isRequired,
    match: PropTypes.shape({
      params: PropTypes.shape({
        slug: PropTypes.string,
      }),
    }).isRequired,
    updateError: PropTypes.func.isRequired,
    location: PropTypes.shape().isRequired,
    // 商品情報 (パンくずリストに必要な情報を含む)
    sitemapInfo: PropTypes.shape({}).isRequired,
    // スクリーンサイズ
    screenSize: PropTypes.number,
  };

  componentWillMount = () => {
    const { data, locale, fetchNewsAndEvent, match, updateError } = this.props;

    if (data.noLoaded) {
      fetchNewsAndEvent(match.params.slug, locale)
        .then(res => {
          const { error } = res.action.payload.data;
          if (error) updateError(error);
          return res;
        })
        .catch(e => {
          errorLogger(e);
        });
    }
  };

  render = () => {
    // 各コンテンツ Placeholder 表示に使用する
    const contentPlaceholderStart = 1;
    const contentPlaceholderEnd = 4;
    // Hero Image 縦幅算出用係数
    const coefficientHeroImage = 1.24;

    const { data, location, locale, match, sitemapInfo, screenSize } = this.props;

    const quickLinks = data.sections.filter(section => section.quickLinkLabel).map(section => ({
      id: section.entryId,
      label: section.quickLinkLabel,
    }));

    return (
      <React.Fragment>
        {_.get(data, 'region') && (
          <Helmet
            link={[
              makeCanonicalTagObj(locale, `/news-and-events/${data.slug}`),
              ...makeHreflangTagArr(data.region, `/news-and-events/${data.slug}`),
            ]}
          />
        )}
        {_.get(data, 'metaData') && (
          <Helmet
            title={_.get(data.metaData, 'title')}
            meta={[
              {
                name: 'description',
                content: _.get(data.metaData, 'description'),
              },
              {
                name: 'keyword',
                content: _.get(data.metaData, 'keyword'),
              },
              // TODO: OGP タグは一旦保留 (2020年01月07日)
              // {
              //   property: 'og:url',
              //   content: _.get(metaData.data, 'url'),
              // },
              // {
              //   property: 'og:type',
              //   content: _.get(metaData.data, 'type'),
              // },
              // {
              //   property: 'og:title',
              //   content: _.get(metaData.data, 'title'),
              // },
              // {
              //   property: 'og:image',
              //   content: _.get(metaData.data, 'image.file.url'),
              // },
              // {
              //   property: 'og:description',
              //   content: _.get(metaData.data, 'description'),
              // },
              // {
              //   property: 'og:site_name',
              //   content: _.get(metaData.data, 'siteName'),
              // },
            ]}
          />
        )}
        {quickLinks.length > 0 && <QuickLinksSection links={quickLinks} />}
        {_.get(data, 'noLoaded') || !_.get(data, 'sections') ? (
          <React.Fragment>
            {/* Placeholder > Overview Section */}
            <PlaceholderWrapper className="overviewSection">
              {/* Placeholder > Overview Section > Hero Image */}
              <Placeholder
                w={screenSize > TABLET_L ? '1050px' : '100%'}
                h={
                  screenSize > TABLET_L ? '650px' : `${(screenSize - MOBILE_PADDING * 2) / 2 * coefficientHeroImage}px`
                }
              />
              {/* Placeholder > Overview Section > date */}
              <Placeholder
                w="70%"
                h={screenSize > TABLET_L ? '96px' : '66px'}
                m={screenSize > TABLET_L ? '40px 0 10px 0' : '25px 0 10px 0'}
              />
              {/* Placeholder > Overview Section > location */}
              <Placeholder w="70%" h="20px" />
              {/* Placeholder > Overview Section > description */}
              <Placeholder w="70%" h="20px" m={screenSize > TABLET_L ? '30px 0 0 0' : '15px 0 0 0'} />
            </PlaceholderWrapper>
            {/* Placeholder > Link Section */}
            <PlaceholderWrapper className="linkSection">
              {/* Placeholder > Link Section */}
              <Placeholder
                w={screenSize > TABLET_L ? '1050px' : '100%'}
                h={
                  screenSize > TABLET_L ? '650px' : `${(screenSize - MOBILE_PADDING * 2) / 2 * coefficientHeroImage}px`
                }
              />
            </PlaceholderWrapper>
            {/* Placeholder > Section Header */}
            <PlaceholderWrapper className="sectionHeader">
              {/* Placeholder > Section Header > title */}
              <Placeholder w="70%" h="27px" m="0 0 30px 0" />
              <Hr />
              {/* Placeholder > Section Header > description */}
              <Placeholder
                w="80%"
                h={screenSize > TABLET_L ? '48px' : '120px'}
                m={screenSize > TABLET_L ? '80px' : '40px'}
              />
            </PlaceholderWrapper>
            {/* Placeholder > Carousel Section */}
            {new Array(contentPlaceholderEnd - contentPlaceholderStart).fill(null).map((item, index) => (
              <PlaceholderWrapper
                direction={screenSize > TABLET_L ? 'row' : 'column'}
                className="carouselSection"
                maxWidth={screenSize > TABLET_L && '1440px'}
                minHeight="100%"
                margin="0 auto 90px"
              >
                <PlaceholderWrapper
                  className={`contentImagePlaceholder ${index === 2 && 'isLast'}`}
                  left={index % 2 !== 0}
                >
                  <Placeholder w="100%" h="100%" m={screenSize > TABLET_L ? '0 0 0 0' : '0 0 20px 0'} />
                </PlaceholderWrapper>
                <PlaceholderWrapper className="contentPlaceholder" left={index % 2 !== 0}>
                  <PlaceholderWrapper className="contentPlaceholderChild">
                    {/* Placeholder > Carousel Section > description */}
                    <PlaceholderWrapper>
                      <Placeholder
                        w={screenSize > TABLET_L ? '100%' : 'calc(100% - 40px)'}
                        h={screenSize > TABLET_L ? '45px' : '36px'}
                        m={screenSize > TABLET_L ? '0 0 20px 0' : '0 0 20px 0'}
                      />
                    </PlaceholderWrapper>
                    {/* Placeholder > Carousel Section > description */}
                    <PlaceholderWrapper>
                      <Placeholder
                        w={screenSize > TABLET_L ? '100%' : 'calc(100% - 40px)'}
                        h={screenSize > TABLET_L ? '45px' : '36px'}
                        m={screenSize > TABLET_L ? '0 0 42px 0' : '0 0 20px 0'}
                      />
                    </PlaceholderWrapper>
                  </PlaceholderWrapper>
                </PlaceholderWrapper>
              </PlaceholderWrapper>
            ))}
          </React.Fragment>
        ) : (
          <React.Fragment>
            {data.sections.map((section, i) => {
              const key = `${section.entryId}-${i}`;
              switch (section.contentType) {
                case 'campaignPageCarouselSection':
                  return <CarouselSection key={key} {...section} />;
                case 'campaignPageGallerySection':
                  return <GallerySection key={key} {...section} />;
                case 'campaignPageLinkSection':
                  return <LinkSection key={key} {...section} />;
                case 'campaignPageOverviewSection':
                  return <OverviewSection key={key} {...section} />;
                case 'campaignPageSectionHeader':
                  return <SectionHeader key={key} {...section} />;
                case 'campaignPageTextPanelsSection':
                  return <TextPanelsSection key={key} {...section} />;
                case 'campaignPageTextSection':
                  return <TextSection key={key} {...section} />;
                case 'campaignPageTopicToutSection':
                  return <TopicToutSection key={key} {...section} />;
                case 'campaignPageVideoSection':
                  return <VideoSection key={key} {...section} />;
                case 'campaignPageImagePanelsSection':
                  return <ImagePanelsSection key={key} {...section} screenSize={screenSize} />;
                case 'campaignPageYoutubeSection':
                  return <YoutubeSection key={key} {...section} />;
                default:
                  return <div key={section.entryId} />;
              }
            })}
          </React.Fragment>
        )}
        <SocialShareSection />
        <ScrollToTopButton />
        {!(locale && location && sitemapInfo && _.get(sitemapInfo, 'label')) ? (
          <Placeholder w="200px" h="24px" m={window.innerWidth > TABLET_L ? '0 0 20px 40px' : '0 0 20px 20px'} />
        ) : (
          <BreadCrumbMenu
            currentPath={`https://www.shiseido-professional.com/${locale}/news-and-events/${match.params.slug}`}
            items={[
              {
                name: 'TOP',
                path: `/${locale}/`,
              },
              {
                name: sitemapInfo.label,
                path: `/${locale}/news-and-events/`,
              },
              {
                name: match.params.slug.toUpperCase(),
                path: null,
              },
            ]}
          />
        )}
      </React.Fragment>
    );
  };
}

const mapState = (state, props) => ({
  // サイトマップ情報をキャッシュしておく
  sitemapInfo: state.siteOptions.sitemap
    ? _.find(state.siteOptions.sitemap, item => item.url === '/news-and-events')
    : {},
  data: getPageData(state, { ...props, dataLevel: 2 }),
  locale: state.locale,
  // スクリーンサイズを実寸で取得する
  screenSize: getScreenWidth(),
});

const mapDispatch = dispatch => ({
  fetchNewsAndEvent: (slug, locale) => dispatch(fetchNewsAndEvent(slug, locale)),
  updateError: error => dispatch({ type: 'UPDATE_ERROR', error }),
});

export default connect(mapState, mapDispatch)(NewsAndEventsDetailsPage);



// WEBPACK FOOTER //
// ./src/pages/NewsAndEventsDetailsPage/NewsAndEventsDetailsPage.js